import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Banknote, CheckCircle2, Landmark, Clock3, ShieldCheck, Loader2, Pencil, Trash2, Users, RotateCcw } from "lucide-react";
import aapi, { inr, fmtDate, formatApiError } from "./aapi";
import { PageHeader, inputCls, selectCls } from "@/panel/components";
import { EASE } from "@/components/Motion";

const STEPS = [
  "Validating beneficiary details",
  "Initiating RTGS instruction",
  "Queued in settlement window",
  "Confirming with beneficiary bank",
];

const EMPTY = { beneficiary: "", account_no: "", ifsc: "", branch: "", amount: "", remark: "" };

export default function AdminTransfer() {
  const [form, setForm] = useState({ ...EMPTY });
  const [phase, setPhase] = useState("form");
  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [funds, setFunds] = useState(null);
  const [fundsEdit, setFundsEdit] = useState(false);
  const [fundsInput, setFundsInput] = useState("");
  const [fundsBusy, setFundsBusy] = useState(false);
  const [delId, setDelId] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  const [payees, setPayees] = useState([]);
  const [editPayee, setEditPayee] = useState(null); // {id, beneficiary, account_no, ifsc, branch, timing_hours}
  const [payeeBusy, setPayeeBusy] = useState(false);
  const timers = useRef([]);

  const load = () => aapi.get("/transfers").then(({ data }) => { setHistory(data.transfers); setFunds(data.funds); }).catch(() => {});
  const loadPayees = () => aapi.get("/payees").then(({ data }) => setPayees(data)).catch(() => {});

  const pickPayee = (id) => {
    const p = payees.find((x) => x.id === id);
    if (!p) return;
    setForm((f) => ({ ...f, beneficiary: p.beneficiary, account_no: p.account_no, ifsc: p.ifsc, branch: p.branch || "" }));
    toast.success(`${p.beneficiary} ki details fill ho gayi`, { description: "Sirf amount daal kar transfer karein." });
  };

  const savePayee = async () => {
    setPayeeBusy(true);
    try {
      const payload = {
        beneficiary: editPayee.beneficiary.trim(), account_no: editPayee.account_no.trim(),
        ifsc: editPayee.ifsc.trim().toUpperCase(), branch: (editPayee.branch || "").trim(),
        timing_hours: editPayee.timing_hours === "" || editPayee.timing_hours === null ? null : parseFloat(editPayee.timing_hours),
      };
      await aapi.put(`/payees/${editPayee.id}`, payload);
      toast.success("Payee update ho gaya");
      setEditPayee(null);
      loadPayees();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPayeeBusy(false);
    }
  };

  const removePayee = async (p) => {
    if (!window.confirm(`Payee "${p.beneficiary}" delete karein? Transfer history safe rahegi.`)) return;
    try {
      await aapi.delete(`/payees/${p.id}`);
      toast.success("Payee deleted");
      loadPayees();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveFundsBase = async () => {
    setFundsBusy(true);
    try {
      const { data } = await aapi.put("/transfers/funds-base", { base: parseFloat(fundsInput) || 0 });
      setFunds(data.funds);
      setFundsEdit(false);
      toast.success("Available funds base updated — audit logged");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setFundsBusy(false);
    }
  };

  const removeTransfer = async (id) => {
    setDelBusy(true);
    try {
      const { data } = await aapi.delete(`/transfers/${id}`);
      if (data.funds) setFunds(data.funds);
      setDelId(null);
      toast.success("Transfer entry deleted — balance auto-adjusted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDelBusy(false);
    }
  };
  useEffect(() => {
    load();
    loadPayees();
    return () => timers.current.forEach(clearTimeout);
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const amt = parseFloat(form.amount);
  const canSubmit =
    form.beneficiary.trim().length >= 3 &&
    /^\d{9,18}$/.test(form.account_no.trim()) &&
    form.ifsc.trim().length === 11 &&
    form.branch.trim().length >= 2 &&
    amt > 0;

  const submit = async () => {
    try {
      const { data } = await aapi.post("/transfers", {
        beneficiary: form.beneficiary.trim(), account_no: form.account_no.trim(),
        ifsc: form.ifsc.trim().toUpperCase(), branch: form.branch.trim(),
        amount: amt, remark: form.remark.trim(),
      });
      setResult(data.transfer);
      if (data.funds) setFunds(data.funds);
      setPhase("processing");
      setStep(0);
      STEPS.forEach((_, i) => timers.current.push(setTimeout(() => setStep(i + 1), 1100 * (i + 1))));
      timers.current.push(setTimeout(() => { setPhase("success"); load(); loadPayees(); }, 1100 * STEPS.length + 600));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reset = () => { setForm({ ...EMPTY }); setResult(null); setPhase("form"); };

  const repeatTransfer = (t) => {
    setForm({
      beneficiary: t.beneficiary || "", account_no: t.account_no || "", ifsc: t.ifsc || "",
      branch: t.branch || "", amount: String(t.amount ?? ""), remark: t.remark || "",
    });
    setPhase("form");
    setResult(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.success(`${t.beneficiary} ka transfer form mein bhar gaya`, { description: "Amount/details check karke dobara transfer karein." });
  };

  const Field = ({ label, children }) => (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );

  return (
    <div data-testid="admin-transfer-page">
      <PageHeader title="Transfer Funds" sub="RTGS bank transfer — account details, IFSC, branch. Processing 0–24 hours." />

      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
        className="max-w-2xl rounded-3xl border border-gold/20 bg-panel/80 p-8">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-gold/30 bg-gold/10">
            <Landmark size={19} className="text-gold" />
          </div>
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] text-gold/70">RTGS FUND TRANSFER</div>
            <p className="mt-1 text-xs text-neutral-500">Real-time gross settlement — beneficiary bank account details bharein.</p>
          </div>
        </div>

        {funds && (
          <div className="mt-6 flex items-center justify-between gap-4 rounded-2xl border border-gold/25 bg-gold/[0.06] px-5 py-4" data-testid="transfer-available-funds">
            <div>
              <div className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.22em] text-neutral-500">
                Available Funds — Site Total
                <button type="button" onClick={() => { setFundsEdit(!fundsEdit); setFundsInput(""); }} data-testid="transfer-funds-edit-btn"
                  aria-label="Edit available funds base"
                  className="text-neutral-600 transition-colors hover:text-gold">
                  <Pencil size={11} />
                </button>
              </div>
              {fundsEdit ? (
                <div className="mt-1.5 flex items-center gap-2">
                  <input value={fundsInput} onChange={(e) => setFundsInput(e.target.value.replace(/[^\d.]/g, ""))}
                    placeholder={String(funds.base)} inputMode="decimal" data-testid="transfer-funds-input"
                    className="w-40 rounded-lg border border-white/10 bg-ink px-2.5 py-1.5 font-mono text-sm text-neutral-200 focus:border-gold focus:outline-none" />
                  <button onClick={saveFundsBase} disabled={fundsBusy || !fundsInput} data-testid="transfer-funds-save"
                    className="rounded-lg bg-gradient-to-br from-goldlight via-gold to-golddark px-3 py-1.5 text-[10px] font-bold text-black disabled:opacity-40">
                    {fundsBusy ? "…" : "SET"}
                  </button>
                </div>
              ) : (
                <div className="mt-1 text-2xl font-bold text-goldlight" data-testid="transfer-available-amount">{inr(funds.available)}</div>
              )}
            </div>
            <div className="text-right">
              <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-neutral-500">Balance after transfers</div>
              <div className="mt-1 text-sm font-semibold text-neutral-200" data-testid="transfer-balance-after">{inr(funds.remaining)}</div>
              <div className="mt-0.5 font-mono text-[9px] text-neutral-600">RTGS out: {inr(funds.transferred)}</div>
            </div>
          </div>
        )}

        <AnimatePresence mode="wait">
          {phase === "form" && (
            <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, y: -14 }}
              className="mt-6 space-y-4" data-testid="transfer-form">
              {payees.length > 0 && (
                <Field label="Saved payee se jaldi bharein">
                  <select defaultValue="" onChange={(e) => { if (e.target.value) pickPayee(e.target.value); e.target.value = ""; }}
                    className={selectCls} data-testid="transfer-payee-select">
                    <option value="">— Payee choose karein (details auto-fill) —</option>
                    {payees.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.beneficiary} · ****{p.account_no.slice(-4)} · {p.ifsc}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              <Field label="Beneficiary name">
                <input value={form.beneficiary} onChange={(e) => set("beneficiary", e.target.value)}
                  placeholder="Account holder name" className={inputCls} data-testid="transfer-beneficiary" />
              </Field>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Account number">
                  <input value={form.account_no} onChange={(e) => set("account_no", e.target.value.replace(/\D/g, "").slice(0, 18))}
                    placeholder="9–18 digits" inputMode="numeric" className={`${inputCls} font-mono`} data-testid="transfer-account-no" />
                </Field>
                <Field label="IFSC code">
                  <input value={form.ifsc} onChange={(e) => set("ifsc", e.target.value.toUpperCase().slice(0, 11))}
                    placeholder="e.g. SBIN0123456" className={`${inputCls} font-mono uppercase`} data-testid="transfer-ifsc" />
                </Field>
              </div>
              <Field label="Branch details">
                <input value={form.branch} onChange={(e) => set("branch", e.target.value)}
                  placeholder="Bank name, branch, city" className={inputCls} data-testid="transfer-branch" />
              </Field>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Amount (₹)">
                  <input type="number" min="1" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)}
                    placeholder="0.00" className={inputCls} data-testid="transfer-amount" />
                </Field>
                <Field label="Remark (optional)">
                  <input value={form.remark} onChange={(e) => set("remark", e.target.value)}
                    placeholder="Payment reference" className={inputCls} data-testid="transfer-remark" />
                </Field>
              </div>

              <div className="flex items-center gap-2.5 rounded-xl border border-gold/20 bg-gold/[0.05] px-4 py-3">
                <Clock3 size={14} className="shrink-0 text-gold" />
                <p className="text-[11px] leading-relaxed text-neutral-400">
                  RTGS transfers are processed within <span className="font-semibold text-goldlight">0–24 hours</span> as per the settlement window.
                </p>
              </div>

              <button onClick={submit} disabled={!canSubmit} data-testid="transfer-submit-button"
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-3.5 text-sm font-bold tracking-[0.12em] text-black disabled:opacity-40">
                <Banknote size={16} />
                TRANSFER VIA RTGS
              </button>
            </motion.div>
          )}

          {phase === "processing" && result && (
            <motion.div key="processing" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }}
              className="mt-6" data-testid="transfer-processing">
              <div className="rounded-2xl border border-gold/20 bg-gold/[0.04] p-6">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] tracking-[0.25em] text-gold/70">PROCESSING RTGS</span>
                  <Loader2 size={16} className="animate-spin text-gold" />
                </div>
                <p className="mt-3 text-2xl font-bold text-neutral-50">{inr(result.amount)}</p>
                <p className="mt-1 text-xs text-neutral-500">{result.beneficiary} · A/C {result.account_no} · {result.ifsc}</p>

                <div className="mt-5 space-y-3">
                  {STEPS.map((s, i) => (
                    <div key={s} className="flex items-center gap-3" data-testid={`transfer-step-${i}`}>
                      {step > i ? (
                        <CheckCircle2 size={15} className="shrink-0 text-gold" />
                      ) : (
                        <span className={`h-3.5 w-3.5 shrink-0 rounded-full border ${step === i ? "animate-pulse border-gold bg-gold/30" : "border-white/15"}`} />
                      )}
                      <span className={`text-xs ${step > i ? "text-neutral-300" : step === i ? "text-goldlight" : "text-neutral-600"}`}>{s}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-5 h-1.5 overflow-hidden rounded-full bg-white/10">
                  <div className="h-full rounded-full bg-gradient-to-r from-goldlight via-gold to-golddark transition-all duration-1000"
                    style={{ width: `${(step / STEPS.length) * 100}%` }} data-testid="transfer-progress" />
                </div>
                <p className="mt-3 flex items-center gap-2 text-[11px] text-neutral-500">
                  <Clock3 size={12} className="text-gold" /> Processing window 0–24 hours · Estimated settlement: {result.settle_time}
                </p>
              </div>
            </motion.div>
          )}

          {phase === "success" && result && (
            <motion.div key="success" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: EASE }} className="mt-6" data-testid="transfer-success">
              <div className="rounded-2xl border border-emerald-400/30 bg-emerald-400/[0.06] p-6 text-center">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 260, damping: 16, delay: 0.15 }}
                  className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-400/15">
                  <CheckCircle2 size={34} className="text-emerald-400" />
                </motion.div>
                <h3 className="mt-4 text-xl font-bold tracking-wide text-emerald-300">TRANSACTION SUCCESSFUL</h3>
                <p className="mt-1 text-xs text-neutral-400">RTGS transfer completed · Settled in {result.settle_time}</p>
                <p className="mt-4 font-mono text-sm font-semibold text-goldlight" data-testid="transfer-utr">UTR: {result.utr}</p>
                {funds && (
                  <p className="mt-3 rounded-xl border border-gold/25 bg-gold/[0.06] px-4 py-2.5 text-xs text-neutral-400" data-testid="transfer-remaining-funds">
                    Remaining funds: <span className="font-bold text-goldlight">{inr(funds.remaining)}</span>
                  </p>
                )}

                <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-3 rounded-xl border border-white/10 bg-black/20 p-4 text-left">
                  {[
                    ["Beneficiary", result.beneficiary],
                    ["Amount", inr(result.amount)],
                    ["Account No.", result.account_no],
                    ["IFSC", result.ifsc],
                    ["Branch", result.branch],
                    ["Mode", result.mode],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-neutral-600">{k}</div>
                      <div className="mt-0.5 truncate text-xs font-medium text-neutral-200" title={String(v)}>{v}</div>
                    </div>
                  ))}
                </div>

                <p className="mt-4 flex items-center justify-center gap-2 text-[10px] text-neutral-500">
                  <ShieldCheck size={12} className="text-gold" /> Reference saved · audit logged
                </p>

                <div className="mt-5 flex gap-2">
                  {result.receipt_url && (
                    <a href={result.receipt_url} target="_blank" rel="noreferrer" data-testid="transfer-receipt-link"
                      className="flex-1 rounded-lg bg-gradient-to-br from-goldlight via-gold to-golddark py-3 text-center text-xs font-bold tracking-[0.12em] text-black transition-opacity hover:opacity-90">
                      DOWNLOAD RECEIPT (PDF)
                    </a>
                  )}
                  <button onClick={reset} data-testid="transfer-new-button"
                    className="flex-1 rounded-xl border border-gold/40 py-3 text-xs font-bold tracking-[0.12em] text-goldlight transition-colors hover:bg-gold/10">
                    NEW TRANSFER
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {payees.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.05 }}
            className="mt-8 max-w-2xl rounded-3xl border border-white/10 bg-panel/80 p-7" data-testid="payees-card">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-gold/30 bg-gold/10">
                <Users size={16} className="text-gold" />
              </div>
              <div>
                <div className="font-mono text-[10px] tracking-[0.25em] text-gold/70">SAVED PAYEES</div>
                <p className="mt-1 text-xs text-neutral-500">Bank jaisa — har transfer ke baad payee khud save ho jata hai. Timing (0–24 hrs) har payee ki alag set kar sakte hain.</p>
              </div>
            </div>
            <div className="mt-5 space-y-2">
              {payees.map((p) => (
                <div key={p.id} className="rounded-2xl border border-white/5 bg-white/[0.02] px-4 py-3" data-testid={`payee-row-${p.id}`}>
                  {editPayee?.id === p.id ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <input value={editPayee.beneficiary} onChange={(e) => setEditPayee({ ...editPayee, beneficiary: e.target.value })}
                          placeholder="Beneficiary name" className={`${inputCls} !py-2.5 text-xs`} data-testid="payee-edit-name" />
                        <input value={editPayee.account_no} onChange={(e) => setEditPayee({ ...editPayee, account_no: e.target.value.replace(/\D/g, "") })}
                          placeholder="Account no" className={`${inputCls} !py-2.5 font-mono text-xs`} data-testid="payee-edit-account" />
                        <input value={editPayee.ifsc} onChange={(e) => setEditPayee({ ...editPayee, ifsc: e.target.value.toUpperCase() })}
                          placeholder="IFSC" className={`${inputCls} !py-2.5 font-mono text-xs uppercase`} data-testid="payee-edit-ifsc" />
                        <input value={editPayee.branch || ""} onChange={(e) => setEditPayee({ ...editPayee, branch: e.target.value })}
                          placeholder="Branch" className={`${inputCls} !py-2.5 text-xs`} data-testid="payee-edit-branch" />
                      </div>
                      <div className="flex items-center gap-2">
                        <input type="number" min="0" max="24" step="0.5" value={editPayee.timing_hours ?? ""}
                          onChange={(e) => setEditPayee({ ...editPayee, timing_hours: e.target.value })}
                          placeholder="Timing (hrs)" className={`${inputCls} !w-36 !py-2.5 font-mono text-xs`} data-testid="payee-edit-timing" />
                        <span className="font-mono text-[9px] tracking-[0.1em] text-neutral-600">0–24 HRS · KHALI = RANDOM</span>
                        <button onClick={savePayee} disabled={payeeBusy} data-testid="payee-edit-save"
                          className="ml-auto rounded-lg bg-gradient-to-br from-goldlight via-gold to-golddark px-4 py-2 text-[10px] font-bold text-black disabled:opacity-40">
                          {payeeBusy ? "…" : "SAVE"}
                        </button>
                        <button onClick={() => setEditPayee(null)} data-testid="payee-edit-cancel"
                          className="rounded-lg border border-white/15 px-3 py-2 text-[10px] text-neutral-400 hover:border-gold/40">
                          CANCEL
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-neutral-100">{p.beneficiary}</p>
                        <p className="mt-0.5 font-mono text-[10px] text-neutral-500">
                          ****{p.account_no.slice(-4)} · {p.ifsc}{p.branch ? ` · ${p.branch}` : ""}
                        </p>
                      </div>
                      <span className="flex items-center gap-1.5 rounded-full border border-gold/25 bg-gold/[0.07] px-2.5 py-1 font-mono text-[9px] tracking-[0.1em] text-goldlight"
                        data-testid={`payee-timing-${p.id}`}>
                        <Clock3 size={11} /> {p.timing_hours != null ? `~${p.timing_hours}h` : "0–24h RANDOM"}
                      </span>
                      <span className="font-mono text-[9px] text-neutral-600">{p.transfer_count || 0} txn</span>
                      <button onClick={() => setEditPayee({ ...p, timing_hours: p.timing_hours ?? "" })} aria-label="Edit payee"
                        data-testid={`payee-edit-${p.id}`}
                        className="text-neutral-500 transition-colors hover:text-goldlight">
                        <Pencil size={13} />
                      </button>
                      <button onClick={() => removePayee(p)} aria-label="Delete payee" data-testid={`payee-delete-${p.id}`}
                        className="text-neutral-600 transition-colors hover:text-rose-300">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

      {history.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
          className="mt-8 overflow-x-auto rounded-2xl border border-white/10" data-testid="transfer-history">
          <div className="min-w-[760px]">
            <div className="grid grid-cols-12 gap-3 border-b border-white/10 bg-white/[0.02] px-5 py-3 font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-500">
              <span className="col-span-3">UTR</span>
              <span className="col-span-2">Beneficiary</span>
              <span className="col-span-2">Amount</span>
              <span className="col-span-2">Settled In</span>
              <span className="col-span-1">Status</span>
              <span className="col-span-1">Date</span>
              <span className="col-span-1 text-right">Actions</span>
            </div>
            {history.map((t) => (
              <div key={t.id} data-testid={`transfer-row-${t.utr}`}
                className="grid grid-cols-12 items-center gap-3 border-b border-white/5 bg-panel/80 px-5 py-3.5 last:border-0">
                <span className="col-span-3 truncate font-mono text-[11px] text-gold/80">{t.utr}</span>
                <span className="col-span-2 truncate text-xs text-neutral-300" title={t.beneficiary}>{t.beneficiary}</span>
                <span className="col-span-2 text-sm font-medium text-neutral-200">{inr(t.amount)}</span>
                <span className="col-span-2 font-mono text-[10px] text-neutral-500">{t.settle_time}</span>
                <span className="col-span-1">
                  <span className="rounded-full bg-emerald-400/15 px-2 py-0.5 font-mono text-[9px] tracking-[0.1em] text-emerald-300">SUCCESS</span>
                </span>
                <span className="col-span-1 font-mono text-[10px] text-neutral-500">{fmtDate(t.created_at)}</span>
                <span className="col-span-1 flex items-center justify-end gap-1.5">
                  <button onClick={() => repeatTransfer(t)} data-testid={`transfer-repeat-${t.utr}`} aria-label="Repeat transfer"
                    title="Isi payee ko dobara transfer — form auto-fill"
                    className="text-neutral-600 transition-colors hover:text-goldlight">
                    <RotateCcw size={13} />
                  </button>
                  {t.receipt_id && (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/statements/file/${t.receipt_id}`} target="_blank" rel="noreferrer"
                      data-testid={`transfer-receipt-${t.utr}`} aria-label="Download receipt"
                      className="inline-flex items-center gap-1 rounded-lg border border-gold/40 px-2 py-1 text-[9px] font-bold text-goldlight transition-colors hover:bg-gold/10">
                      PDF
                    </a>
                  )}
                  {delId === t.id ? (
                    <button onClick={() => removeTransfer(t.id)} disabled={delBusy} data-testid={`transfer-delete-confirm-${t.utr}`}
                      className="rounded-lg bg-rose-400/15 px-2 py-1 text-[9px] font-bold text-rose-300 transition-colors hover:bg-rose-400/25 disabled:opacity-50">
                      {delBusy ? "…" : "SURE?"}
                    </button>
                  ) : (
                    <button onClick={() => { setDelId(t.id); setTimeout(() => setDelId((c) => (c === t.id ? null : c)), 4000); }}
                      data-testid={`transfer-delete-${t.utr}`} aria-label="Delete transfer"
                      className="text-neutral-600 transition-colors hover:text-rose-400">
                      <Trash2 size={13} />
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}
